import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { eq } from 'tio-ui/utilities';
import {
  isPslfDebugActive,
  getPslfDebugState,
  setPslfDebugState,
  clearPslfDebugState,
  getDefaultDebugState,
  PSLF_DEBUG_PRESETS,
  DEFAULT_DEBUG_LOAN,
  DEFAULT_DEBUG_FORM,
  DEBUG_FORM_STATUSES,
  makeDebugFormId,
} from 'tio-employee/utils/pslf/debug-scenarios';
import type {
  PslfDebugState,
  DebugLoan,
  DebugForm,
  DebugFormStatus,
} from 'tio-employee/utils/pslf/debug-scenarios';
import {
  PhBug,
  PhCaretDown,
  PhCaretUp,
  PhArrowCounterClockwise,
  PhPlus,
  PhTrash,
} from 'ember-phosphor-icons';
import { service } from '@ember/service';
import type FeaturesService from 'tio-common/services/features';

import type Owner from '@ember/owner';

export interface PslfDebugPanelSignature {
  Element: HTMLDivElement;
}

export default class PslfDebugPanel extends Component<PslfDebugPanelSignature> {
  @service declare features: FeaturesService;

  @tracked isCollapsed = true;
  @tracked state: PslfDebugState;

  constructor(owner: Owner, args: Record<string, never>) {
    super(owner, args);
    this.state = isPslfDebugActive() ? getPslfDebugState() : getDefaultDebugState();
  }

  get isAvailable() {
    return this.features.isEnabled('pslfDebugPanel');
  }

  get isActive() {
    return isPslfDebugActive();
  }

  get formStatusOptions() {
    return DEBUG_FORM_STATUSES;
  }

  get presetEntries() {
    return Object.entries(PSLF_DEBUG_PRESETS).map(([key, preset]) => ({
      key,
      label: preset.label,
    }));
  }

  toggleCollapsed = () => {
    this.isCollapsed = !this.isCollapsed;
  };

  // ---------------------------------------------------------------------------
  // Form editing
  // ---------------------------------------------------------------------------

  addForm = () => {
    const forms = [...this.state.debugForms];
    const hasCurrentEmployer = forms.some((f) => f.formType === 'currentEmployer');
    const newForm: DebugForm = {
      ...DEFAULT_DEBUG_FORM,
      id: makeDebugFormId(),
      employerName: `Employer ${forms.length + 1}`,
    };
    if (hasCurrentEmployer) {
      // If a current employer exists, make this a prior employer with a distinct date range
      newForm.formType = 'priorEmployer';
      const current = forms.find((f) => f.formType === 'currentEmployer');
      const currentStart = current?.employmentStartDate;
      if (currentStart) {
        // End date is 1 month before current employer's employment start
        const endDate = new Date(currentStart);
        endDate.setMonth(endDate.getMonth() - 1);
        newForm.employmentEndDate = endDate.toISOString().slice(0, 10);
        // Start date is 1 year before the end date
        const startDate = new Date(endDate);
        startDate.setFullYear(startDate.getFullYear() - 1);
        newForm.employmentStartDate = startDate.toISOString().slice(0, 10);
      } else {
        // fallback: 2 years ago to 1 year ago
        const endDate = new Date();
        endDate.setFullYear(endDate.getFullYear() - 1);
        newForm.employmentEndDate = endDate.toISOString().slice(0, 10);
        const startDate = new Date(endDate);
        startDate.setFullYear(startDate.getFullYear() - 1);
        newForm.employmentStartDate = startDate.toISOString().slice(0, 10);
      }
    }
    forms.push(newForm);
    this.state = { ...this.state, debugForms: forms };
  };

  removeForm = (index: number) => {
    const forms = this.state.debugForms.filter((_, i) => i !== index);
    this.state = { ...this.state, debugForms: forms };
  };

  /** Convert an ISO date string to YYYY-MM-DD for HTML date inputs */
  formatDateForInput = (iso: string | null | undefined): string => {
    if (!iso) return '';
    return iso.slice(0, 10);
  };

  updateFormField = (index: number, field: keyof DebugForm, event: Event) => {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const forms = [...this.state.debugForms];
    const form = { ...forms[index]! };
    if (field === 'formType') {
      form.formType = target.value as 'currentEmployer' | 'priorEmployer';
    } else if (field === 'status') {
      form.status = target.value as DebugFormStatus;
    } else if (field === 'employerName') {
      form.employerName = target.value;
    } else if (
      field === 'borrowerCompletedAt' ||
      field === 'employerCompletedAt' ||
      field === 'submittedAt'
    ) {
      form[field] = target.value ? new Date(target.value).toISOString() : null;
    } else if (field === 'employmentStartDate' || field === 'employmentEndDate') {
      form[field] = target.value || null;
    } else if (field === 'nonPartner') {
      form.nonPartner = (target as HTMLInputElement).checked;
    }
    forms[index] = form;
    this.state = { ...this.state, debugForms: forms };
  };

  toggleFlag = (flag: keyof PslfDebugState) => {
    this.state = { ...this.state, [flag]: !this.state[flag] };
  };

  addLoan = () => {
    const loans = [
      ...this.state.debugLoans,
      { ...DEFAULT_DEBUG_LOAN, name: `Loan ${this.state.debugLoans.length + 1}` },
    ];
    this.state = { ...this.state, debugLoans: loans };
  };

  removeLoan = (index: number) => {
    const loans = this.state.debugLoans.filter((_, i) => i !== index);
    this.state = {
      ...this.state,
      debugLoans: loans.length > 0 ? loans : [{ ...DEFAULT_DEBUG_LOAN }],
    };
  };

  updateLoanField = (index: number, field: keyof DebugLoan, event: Event) => {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const loans = [...this.state.debugLoans];
    const loan = { ...loans[index]! };
    if (field === 'currentBalance' || field === 'qualifyingPayments') {
      loan[field] = Number(target.value) || 0;
    } else if (field === 'loanType') {
      loan.loanType = target.value as 'direct' | 'ffel';
    } else if (field === 'servicer') {
      loan.servicer = target.value;
    } else {
      loan.name = target.value;
    }
    loans[index] = loan;
    this.state = { ...this.state, debugLoans: loans };
  };

  applyPreset = (presetKey: string) => {
    const preset = PSLF_DEBUG_PRESETS[presetKey];
    if (!preset) return;
    // For NSLDS-related presets, preserve existing debugForms
    const nsldsPresets = ['withNslds', 'noNslds', 'estimatorDone'];
    const newState: PslfDebugState = nsldsPresets.includes(presetKey)
      ? {
          ...getDefaultDebugState(),
          ...preset.state,
          debugForms: this.state.debugForms,
        }
      : { ...getDefaultDebugState(), ...preset.state };
    setPslfDebugState(newState);
  };

  applyState = () => {
    setPslfDebugState({ ...this.state, enabled: true });
  };

  resetToLive = () => {
    clearPslfDebugState();
  };

  <template>
    {{! Not translating this file; it is purely a developer debugging tool and will never be customer-facing. }}
    {{! template-lint-disable no-bare-strings }}
    {{#if this.isAvailable}}
      <div
        class="no-replay fixed bottom-4 right-4 z-9999 font-sans text-xs"
        data-test-pslf-debug-panel
        ...attributes
      >
        {{! Collapsed button }}
        {{#if this.isCollapsed}}
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg border
              {{if
                this.isActive
                'bg-amber-50 border-amber-300 text-amber-800'
                'bg-gray-50 border-gray-300 text-gray-600'
              }}
              hover:shadow-xl transition-shadow"
            {{on "click" this.toggleCollapsed}}
          >
            <PhBug @size={{14}} />
            <span class="font-semibold">PSLF Debug</span>
            {{#if this.isActive}}
              <span class="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            {{/if}}
            <PhCaretUp @size={{12}} />
          </button>
        {{else}}
          {{! Expanded panel }}
          <div
            class="w-80 max-h-[80vh] overflow-y-auto rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            {{! Header }}
            <div
              class="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-xl"
            >
              <div class="flex items-center gap-1.5">
                <PhBug @size={{14}} class="text-amber-600" />
                <span class="font-bold text-sm">PSLF Debug Panel</span>
                {{#if this.isActive}}
                  <span
                    class="px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 text-[10px] font-semibold"
                  >ACTIVE</span>
                {{/if}}
              </div>
              <button
                type="button"
                class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                {{on "click" this.toggleCollapsed}}
              >
                <PhCaretDown @size={{14}} />
              </button>
            </div>

            <div class="p-3 space-y-4">
              {{! Presets }}
              <div>
                <div
                  class="font-semibold text-[11px] uppercase tracking-wider text-gray-500 mb-1.5"
                >Quick Presets</div>
                <div class="flex flex-wrap gap-1">
                  {{#each this.presetEntries as |entry|}}
                    <button
                      type="button"
                      class="px-2 py-1 rounded-md border border-gray-200 dark:border-gray-600 hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:border-violet-300 text-[11px] transition-colors"
                      data-test-pslf-debug-preset={{entry.key}}
                      {{on "click" (fn this.applyPreset entry.key)}}
                    >
                      {{entry.label}}
                    </button>
                  {{/each}}
                </div>
              </div>

              {{! PSLF Forms Editor }}
              <div>
                <div
                  class="font-semibold text-[11px] uppercase tracking-wider text-gray-500 mb-1.5"
                >PSLF Forms ({{this.state.debugForms.length}})</div>
                <div class="space-y-2">
                  {{#each this.state.debugForms as |form index|}}
                    <div
                      class="p-2 rounded-lg border border-violet-200 dark:border-violet-700 bg-violet-50 dark:bg-violet-900/20 space-y-1.5"
                    >
                      <div class="flex items-center gap-1">
                        <input
                          type="text"
                          value={{form.employerName}}
                          class="flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                          placeholder="Employer name"
                          {{on "change" (fn this.updateFormField index "employerName")}}
                        />
                        <button
                          type="button"
                          class="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
                          title="Remove form"
                          {{on "click" (fn this.removeForm index)}}
                        >
                          <PhTrash @size={{12}} />
                        </button>
                      </div>
                      <div class="text-[10px] text-gray-500 mt-1">
                        Employment:
                        {{form.employmentStartDate}}
                        -
                        {{#if (eq form.formType "currentEmployer")}}
                          Present
                        {{else}}
                          {{form.employmentEndDate}}
                        {{/if}}
                      </div>
                      <div class="flex gap-2">
                        <div class="flex-1 flex items-center mt-1">
                          <input
                            type="checkbox"
                            checked={{form.nonPartner}}
                            {{on "change" (fn this.updateFormField index "nonPartner")}}
                            class="mr-1"
                          />
                          <span class="text-[11px]">Non-Partner</span>
                        </div>
                        <div class="flex-1">
                          <div class="text-[10px] text-gray-500">Type</div>
                          <select
                            class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1 py-1 text-xs"
                            {{on "change" (fn this.updateFormField index "formType")}}
                          >
                            <option
                              value="currentEmployer"
                              selected={{eq form.formType "currentEmployer"}}
                            >Current</option>
                            <option
                              value="priorEmployer"
                              selected={{eq form.formType "priorEmployer"}}
                            >Prior</option>
                          </select>
                        </div>
                        <div class="flex-1">
                          <div class="text-[10px] text-gray-500">Status</div>
                          <select
                            class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1 py-1 text-xs"
                            {{on "change" (fn this.updateFormField index "status")}}
                          >
                            {{#each this.formStatusOptions as |opt|}}
                              <option
                                value={{opt.value}}
                                selected={{eq opt.value form.status}}
                              >{{opt.label}}</option>
                            {{/each}}
                          </select>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <div class="text-[10px] text-gray-500">Borrower Signed</div>
                          <input
                            type="date"
                            value={{this.formatDateForInput form.borrowerCompletedAt}}
                            class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                            {{on "change" (fn this.updateFormField index "borrowerCompletedAt")}}
                          />
                        </div>
                        <div class="flex-1">
                          <div class="text-[10px] text-gray-500">Employer Signed</div>
                          <input
                            type="date"
                            value={{this.formatDateForInput form.employerCompletedAt}}
                            class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                            {{on "change" (fn this.updateFormField index "employerCompletedAt")}}
                          />
                        </div>
                      </div>
                      <div>
                        <div class="text-[10px] text-gray-500">Submitted</div>
                        <input
                          type="date"
                          value={{this.formatDateForInput form.submittedAt}}
                          class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                          {{on "change" (fn this.updateFormField index "submittedAt")}}
                        />
                      </div>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <div class="text-[10px] text-gray-500">Employment Start</div>
                          <input
                            type="date"
                            value={{form.employmentStartDate}}
                            class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                            {{on "change" (fn this.updateFormField index "employmentStartDate")}}
                          />
                        </div>
                        <div class="flex-1">
                          <div class="text-[10px] text-gray-500">Employment End</div>
                          <input
                            type="date"
                            value={{form.employmentEndDate}}
                            class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                            {{on "change" (fn this.updateFormField index "employmentEndDate")}}
                          />
                        </div>
                      </div>
                    </div>
                  {{/each}}
                </div>
                <button
                  type="button"
                  class="mt-1.5 flex items-center gap-1 text-[11px] text-violet-600 hover:text-violet-800"
                  {{on "click" this.addForm}}
                >
                  <PhPlus @size={{12}} />
                  Add Form
                </button>
              </div>

              {{! NSLDS Data Toggle + Loan Editor }}
              <div>
                <label class="flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    class="rounded border-gray-300 dark:border-gray-600 text-blue-600"
                    checked={{this.state.hasNsldsData}}
                    data-test-pslf-debug-toggle-has-nslds-data
                    {{on "change" (fn this.toggleFlag "hasNsldsData")}}
                  />
                  <span class="font-medium">NSLDS Uploaded</span>
                </label>

                <div class="{{unless this.state.hasNsldsData 'opacity-40 pointer-events-none'}}">
                  <div
                    class="font-semibold text-[11px] uppercase tracking-wider text-gray-500 mb-1.5"
                  >Loans</div>
                  <div class="space-y-2">
                    {{#each this.state.debugLoans as |loan index|}}
                      <div
                        class="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 space-y-1.5"
                      >
                        <div class="flex items-center gap-1">
                          <input
                            type="text"
                            value={{loan.name}}
                            class="flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                            placeholder="Loan name"
                            {{on "change" (fn this.updateLoanField index "name")}}
                          />
                          <select
                            class="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1 py-1 text-xs"
                            {{on "change" (fn this.updateLoanField index "loanType")}}
                          >
                            <option
                              value="direct"
                              selected={{eq loan.loanType "direct"}}
                            >Direct</option>
                            <option value="ffel" selected={{eq loan.loanType "ffel"}}>FFEL</option>
                          </select>
                          <button
                            type="button"
                            class="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
                            title="Remove loan"
                            {{on "click" (fn this.removeLoan index)}}
                          >
                            <PhTrash @size={{12}} />
                          </button>
                        </div>
                        <div class="flex gap-2">
                          <div class="flex-1">
                            <div class="text-[10px] text-gray-500">Balance</div>
                            <input
                              type="number"
                              value={{loan.currentBalance}}
                              class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                              {{on "change" (fn this.updateLoanField index "currentBalance")}}
                            />
                          </div>
                          <div class="flex-1">
                            <div class="text-[10px] text-gray-500">Qualifying Payments</div>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={{loan.qualifyingPayments}}
                              class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                              {{on "change" (fn this.updateLoanField index "qualifyingPayments")}}
                            />
                          </div>
                        </div>
                        <div class="flex gap-2">
                          <div class="flex-1">
                            <div class="text-[10px] text-gray-500">Servicer</div>
                            <input
                              type="text"
                              value={{loan.servicer}}
                              class="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-xs"
                              placeholder="e.g. Mohela"
                              {{on "change" (fn this.updateLoanField index "servicer")}}
                            />
                          </div>
                        </div>
                      </div>
                    {{/each}}
                  </div>
                  <button
                    type="button"
                    class="mt-1.5 flex items-center gap-1 text-[11px] text-violet-600 hover:text-violet-800"
                    {{on "click" this.addLoan}}
                  >
                    <PhPlus @size={{12}} />
                    Add Loan
                  </button>
                </div>
              </div>

              {{! Estimator }}
              <div>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    class="rounded border-gray-300 dark:border-gray-600 text-violet-600"
                    checked={{this.state.estimatorCompleted}}
                    data-test-pslf-debug-toggle-estimator-completed
                    {{on "change" (fn this.toggleFlag "estimatorCompleted")}}
                  />
                  <span class="font-medium">Estimator Completed</span>
                </label>
              </div>

              {{! Recommendations }}
              <div>
                <div
                  class="font-semibold text-[11px] uppercase tracking-wider text-gray-500 mb-1.5"
                >Recommendations</div>
                <label class="flex items-center gap-2 cursor-pointer mb-1.5">
                  <input
                    type="checkbox"
                    class="rounded border-gray-300 dark:border-gray-600 text-violet-600"
                    checked={{this.state.showRecommendations}}
                    data-test-pslf-debug-toggle-show-recommendations
                    {{on "change" (fn this.toggleFlag "showRecommendations")}}
                  />
                  <span class="font-medium">Show Section</span>
                </label>
                <div
                  class="ml-4 space-y-1
                    {{unless this.state.showRecommendations 'opacity-40 pointer-events-none'}}"
                >
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                      checked={{this.state.consolidateLoans}}
                      data-test-pslf-debug-toggle-consolidate-loans
                      {{on "change" (fn this.toggleFlag "consolidateLoans")}}
                    />
                    <span>Consolidate Loans</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                      checked={{this.state.missingRepaymentPlan}}
                      data-test-pslf-debug-toggle-missing-repayment-plan
                      {{on "change" (fn this.toggleFlag "missingRepaymentPlan")}}
                    />
                    <span>Missing Repayment Plan</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                      checked={{this.state.changeRepaymentPlan}}
                      data-test-pslf-debug-toggle-change-repayment-plan
                      {{on "change" (fn this.toggleFlag "changeRepaymentPlan")}}
                    />
                    <span>Change Repayment Plan</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                      checked={{this.state.linkLoans}}
                      data-test-pslf-debug-toggle-link-loans
                      {{on "change" (fn this.toggleFlag "linkLoans")}}
                    />
                    <span>Link Loans</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                      checked={{this.state.recertifyIncome}}
                      data-test-pslf-debug-toggle-recertify-income
                      {{on "change" (fn this.toggleFlag "recertifyIncome")}}
                    />
                    <span>Recertify Income</span>
                  </label>
                </div>
              </div>

              {{! Actions }}
              <div class="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  class="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 transition-colors"
                  data-test-pslf-debug-apply
                  {{on "click" this.applyState}}
                >
                  Apply &amp; Reload
                </button>
                <button
                  type="button"
                  class="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Reset to live data"
                  data-test-pslf-debug-reset
                  {{on "click" this.resetToLive}}
                >
                  <PhArrowCounterClockwise @size={{12}} />
                  Live
                </button>
              </div>
            </div>
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
